import { useMemo, useState } from 'react';
import type { EmailTemplateType } from '../../../../lib/api';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { AlertCircle, Loader2, Mail, RefreshCw, Search } from 'lucide-react';

/** Categories first in this order; anything else follows alphabetically. */
const CATEGORY_ORDER = [
  'Purchase Orders', 'Quality', 'Suppliers', 'RFQ', 'Users', 'Documents',
  'Onboarding', 'Call-Off', 'Questionnaire', 'System',
];

interface NotificationTemplateListProps {
  grouped: Record<string, EmailTemplateType[]> | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selected: string | null;
  onSelect: (type: EmailTemplateType) => void;
}

/**
 * Notification email types, grouped by category, with a search box. Types
 * without a preview sample stay selectable and are marked, so the list is the
 * full inventory of what the portal can send.
 */
export default function NotificationTemplateList({
  grouped, loading, error, onRetry, selected, onSelect,
}: NotificationTemplateListProps) {
  const [query, setQuery] = useState('');

  const categories = useMemo(() => {
    const source = grouped ?? {};
    const names = Object.keys(source).sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    const needle = query.trim().toLowerCase();
    return names
      .map((name) => ({
        name,
        types: (source[name] ?? []).filter((t) => !needle || t.label.toLowerCase().includes(needle)
          || name.toLowerCase().includes(needle)),
      }))
      .filter((c) => c.types.length > 0);
  }, [grouped, query]);

  const total = Object.values(grouped ?? {}).reduce((n, list) => n + list.length, 0);

  return (
    <Card className="w-[290px] shrink-0 overflow-hidden self-start">
      <CardHeader className="pb-3 space-y-2">
        <CardTitle className="text-sm font-medium">
          Notification emails{total > 0 ? ` (${total})` : ''}
        </CardTitle>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search emails"
            aria-label="Search notification emails"
            className="pl-8 h-9"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading && (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading email types">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-10 px-4 text-sm">
            <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
            <p className="font-medium">Could not load email types</p>
            <p className="text-xs text-muted-foreground mt-1">{error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              <RefreshCw className="h-4 w-4 mr-1" /> Try again
            </Button>
          </div>
        )}
        {!loading && !error && total === 0 && (
          <p className="text-center py-12 px-4 text-sm text-muted-foreground">No notification emails are defined.</p>
        )}
        {!loading && !error && total > 0 && categories.length === 0 && (
          <p className="text-center py-12 px-4 text-sm text-muted-foreground">
            No emails match &ldquo;{query.trim()}&rdquo;.
          </p>
        )}
        {!loading && !error && categories.length > 0 && (
          <div className="overflow-y-auto max-h-[calc(100vh-22rem)]">
            {categories.map(({ name, types }) => (
              <div key={name}>
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 border-y border-border">
                  {name}
                </div>
                {types.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => onSelect(t)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-border/50 flex items-center gap-2 ${
                      selected === t.value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    <span className="flex-1 min-w-0 truncate">{t.label}</span>
                    {!t.has_sample && (
                      <Badge variant="outline" className="text-[10px] font-normal shrink-0" title="No preview sample yet">
                        No sample
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
